import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Language, LanguageFormProps } from './Languages.d'
import { Card } from 'primereact/card'
import { api } from '../../../../auth'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import SelectField from '../../../common/forms/fields/SelectField'
import { languageDirections } from '../../../common/Constants'
import TextField from '../../../common/forms/fields/TextField'
import { setLanguages } from '../../../../features/security/security.slice'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleNotch,
    faHandPointRight,
} from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'

const LanguageForm: FC<LanguageFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = api

    const dispatch = useAppDispatch()

    const emptyLanguage: Language = {
        countries_id: null,
        locales_id: null,
        name: '',
        direction: 'ltr',
        default: false,
        active: false,
        available: false,
    }

    const LanguageFormSchema = Yup.object().shape({
        name: Yup.string().trim().required('Enter language name'),
        locales_id: Yup.string().trim().required('Select a locale'),
        countries_id: Yup.string().trim().required('Select a country'),
        direction: Yup.string().trim().required('Select text direction'),
    })

    const [languageDialog, setLanguageDialog] = useState(true)
    const [language, setLanguage] = useState<Language>(emptyLanguage)
    const [countries, setCountries] = useState<[]>([])
    const [locales, setLocales] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/administration/languages/' + id
            : '/administration/languages/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data.language) {
                    setLanguage(response.data.data.language)
                }

                setCountries(response.data.data.countries || [])
                setLocales(response.data.data.locales || [])
                // setTranslations(response.data.data.translations)
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load language')
            }
        }
    }, [axiosPrivate, id, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/administration/languages/' + id
                : '/administration/languages/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                dispatch(setLanguages(response.data.data.languages))
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const hideDialog = () => {
        setLanguageDialog(false)
        navigate('/administration/languages')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/administration/languages/delete/' + id}
                    record={language}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete language:
                                <strong> {language.name}</strong>?
                            </p>
                            <Alert variant="warning" className="border-0">
                                <strong>Warning!</strong> All codes related to
                                this language will be deleted permenantly!
                            </Alert>
                        </>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'language-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...language,
                }}
                validationSchema={LanguageFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    return (
                        <FormikForm id="language-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formLanguageName"
                                                    name="name"
                                                    label="Name"
                                                    placeholder="Enter language name"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                                <SelectField
                                                    id="formTextDirection"
                                                    name="direction"
                                                    label="Text Direction"
                                                    formikProps={formikProps}
                                                    choices={languageDirections}
                                                    required
                                                />
                                            </Col>

                                            <Col xl={6}>
                                                <SelectField
                                                    id="formLocale"
                                                    name="locales_id"
                                                    label="Locale"
                                                    formikProps={formikProps}
                                                    choices={locales}
                                                    required
                                                />
                                                <SelectField
                                                    id="formCountry"
                                                    name="countries_id"
                                                    label="Country"
                                                    formikProps={formikProps}
                                                    choices={countries}
                                                    required
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xl={4}></Col>
                                        </Row>
                                    </fieldset>
                                    <fieldset>
                                        <legend>Status</legend>
                                        <Row>
                                            <Col xl={4}>
                                                <Form.Group
                                                    controlId={
                                                        'formLanguageActiveFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Active"
                                                        name="active"
                                                        checked={
                                                            formikProps.values
                                                                .active
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'active',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={4}>
                                                <Form.Group
                                                    controlId={
                                                        'formLanguageAvailableFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Available on web"
                                                        name="available"
                                                        checked={
                                                            formikProps.values
                                                                .available
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'available',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={4}>
                                                <Form.Group
                                                    controlId={
                                                        'formLanguageDefaultFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Default"
                                                        name="default"
                                                        checked={
                                                            formikProps.values
                                                                .default
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'default',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={12}>
                                                <Alert
                                                    variant={
                                                        formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info'
                                                    }
                                                    className={
                                                        (formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info') +
                                                        ' mt-3 mb-1'
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faHandPointRight}
                                                        className="me-2"
                                                    />{' '}
                                                    When a language is set to{' '}
                                                    <strong>default</strong> the
                                                    system will automatically
                                                    mark it as{' '}
                                                    <strong>Active</strong> and{' '}
                                                    <strong>
                                                        Available on web
                                                    </strong>
                                                </Alert>
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>

                            <input
                                type="submit"
                                id="language-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={languageDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '60vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : language.name
                                : 'Create new language'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : language.name
                                : 'Create new language'
                        }
                    />
                    {FormContent}
                    {formFooter}
                </>
            )}
        </>
    )
}

export default LanguageForm
